import React, { Component } from 'react';
import ReactDOM from 'react-dom';

import i18n from 'i18n-react';
import * as Sentry from '@sentry/browser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  APIHandler,
  AppRouter,
  CookieWarning,
  DataHandler,
  MobileWarning,
  StorageHandler,
  SweetAlert,
  locales,
} from 'C4';
import { storageKeys, language, sentry } from 'settings';
import AppSettings from 'config/AppSettings';

import 'normalize.css/normalize.css';
import { SettingsProvider } from 'hooks/useSettings';
import { UserInfoProvider } from 'hooks/useUserInfo';

import 'config/FontAwesome';
import 'assets/styles/styles.scss';

Sentry.init(sentry);

class App extends Component {
  constructor(props) {
    super(props);

    const mobileRegex = new RegExp(
      'Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini',
      'i',
    );
    const mobilePathRegex = new RegExp('mobile', 'i');

    this.state = {
      onMobile:
        mobileRegex.test(window.navigator.userAgent) && !mobilePathRegex.test(window.location.href),
      askedBefore: null,
      appLanguage: null,
      appId: null,
      loading: true,
    };

    this.setAsked = this.setAsked.bind(this);
  }

  componentDidMount() {
    this.setLoader();
    this.checkMobile();
    this.setLanguage();
    this.getAppId();
    this.setBaseHref();
  }

  setBaseHref() {
    const baseUrl = window.location.origin;

    // Check if the current domain is not "www.bvirevealed.com'
    // if (window.location.hostname !== 'www.bvirevealed.com') {
    // Create a base element
    const baseElement = document.createElement('base');
    baseElement.href = baseUrl;

    // Insert the base element into the document head
    document.head.appendChild(baseElement);
    // }
  }

  componentDidUpdate() {
    const { appLanguage } = this.state;
    const { texts } = i18n;

    if (JSON.stringify(appLanguage) !== JSON.stringify(texts)) {
      i18n.setTexts(appLanguage);
    }
  }

  setLoader() {
    AppSettings().then((res) => {
      if (res) {
        this.setState({ loading: false });
      }
    });
  }

  async getAppId() {
    const appId = await DataHandler.matchAppId();
    const localId = await StorageHandler.load(storageKeys.appId);

    if (localId !== appId) {
      StorageHandler.removeKeys(
        Object.keys(storageKeys).filter(
          (key) => ![storageKeys.appId, storageKeys.lang].includes(storageKeys[key]),
        ),
      );
    }

    StorageHandler.save(storageKeys.appId, appId, 86400000 * 365);
    this.setState({ appId });
  }

  async getLocation() {
    const { countryCode, country } = (await APIHandler.getLocation()) || {};
    const languageData = {
      country: 'United States',
      code: 'US',
      langFile: 'en',
    };

    const availableLanguages = language
      .map(({ code }) => (typeof code === 'string' ? code : code.map((c) => c.toLowerCase())))
      .flat();

    if (countryCode && country && availableLanguages.includes(countryCode.toLowerCase())) {
      const [file] = language
        .filter(({ code }) =>
          typeof code === 'string' ? code === countryCode : code.includes(code),
        )
        .map(({ langFile }) => langFile);

      Object.assign(languageData, {
        country: country.replace(/[ ]/g, '_'),
        code: countryCode.toLowerCase(),
        langFile: file,
      });
    }

    StorageHandler.save(storageKeys.lang, languageData);
    this.setState({ appLanguage: locales[languageData.langFile] || locales.en });
  }

  async setLanguage() {
    const languageExists = await StorageHandler.exists(storageKeys.lang);
    if (languageExists) {
      const { langFile } = (await StorageHandler.load(storageKeys.lang)) || {};
      this.setState({ appLanguage: locales[langFile] || locales.en });
    } else {
      this.getLocation();
    }
  }

  setAsked() {
    this.setState({ askedBefore: true });
    StorageHandler.save('askedMobile', true);
  }

  async checkMobile() {
    const askedBefore = await StorageHandler.load('askedMobile');
    this.setState({ askedBefore });
  }

  render() {
    const { onMobile, askedBefore, appLanguage, appId, loading } = this.state;

    return (
      <SettingsProvider>
        <UserInfoProvider>
          <div className="container__router">
            {!window.navigator.cookieEnabled && <CookieWarning />}
            {loading && (
              <div className="appLoader">
                <FontAwesomeIcon icon={['fas', 'circle-notch']} spin />
              </div>
            )}
            {(!onMobile || askedBefore) && !loading && appId && (
              <AppRouter key={JSON.stringify(appLanguage)} />
            )}
            {onMobile && !askedBefore && (
              <SweetAlert
                trigger={onMobile}
                {...{
                  title: i18n.translate('onMobile.header'),
                  html: <MobileWarning />,
                  customClass: 'mobileWarning',
                  confirmButtonText: i18n.translate('onMobile.continue'),
                  onClose: this.setAsked,
                }}
              />
            )}
          </div>
        </UserInfoProvider>
      </SettingsProvider>
    );
  }
}

ReactDOM.render(<App />, document.getElementById('root'));
